/** /bus-stops/:id/:tab? — bus-stop editor shell. Tabs: General + Used by (the
 *  line set(s) whose routes contain this stop). Delete is blocked with 409
 *  `route_stop_in_use` when a route references the stop — surface the
 *  "remove it from every line first" message. */
import DeleteIcon from '@mui/icons-material/Delete';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Tab, Tabs } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as routeStopsApi from '@/api/routeStops';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { PageHeader } from '@/components/PageHeader';
import { IdentitySubtitle } from '@/components/ValueActions/identity';
import { busStopsHref } from '@/utils/gridLinks';
import { PermissionGate } from '@/components/PermissionGate';
import { useSnackbar } from '@/components/Snackbar';
import { UsedByPanel } from '@/components/UsedByPanel/UsedByPanel';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';

import { BusStopGeneralTab } from './tabs/BusStopGeneralTab';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'used-by', label: 'Used by' },
] as const;

const IN_USE_MESSAGE =
  'This bus stop is used by a line and can’t be deleted. Remove it from every line first.';

export function BusStopDetailPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const id = Number(idStr);
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const { hasPerm } = usePermissions();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    data: stop,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['route_stops', 'detail', id],
    queryFn: () => routeStopsApi.retrieve(id),
    enabled: Number.isFinite(id),
  });
  const onSaved = () => void refetch();

  const deleteMutation = useMutation({
    mutationFn: () => routeStopsApi.remove(id),
    onSuccess: () => {
      snackbar.show('Bus stop deleted.', 'success');
      qc.invalidateQueries({ queryKey: ['route_stops'] });
      navigate('/bus-stops');
    },
    onError: (e) => {
      if (e instanceof ConsoleApiError && e.status === 409) {
        const code = (e.body as { code?: string } | undefined)?.code;
        snackbar.show(code === 'route_stop_in_use' ? IN_USE_MESSAGE : e.message, 'error');
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error');
    },
  });

  const canViewHistory = hasPerm('console_audit_view');
  const tabs = useMemo(
    () => (canViewHistory ? [...TABS, { id: 'history', label: 'History' } as const] : [...TABS]),
    [canViewHistory],
  );
  const activeTab = useMemo(() => tabs.find((t) => t.id === tab)?.id ?? 'general', [tabs, tab]);

  if (!Number.isFinite(id)) return <Alert severity="warning">Invalid bus-stop id.</Alert>;
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !stop) return <Alert severity="error">Couldn’t load bus stop.</Alert>;

  return (
    <Box>
      <PageHeader
        helpStory="bus-stops-line-sets"
        section={tabs.find((t) => t.id === activeTab)?.label}
        title={stop.name}
        subtitle={
          <IdentitySubtitle
            tenant={stop.tenant}
            grid={{ href: busStopsHref({ tenant: stop.tenant.id }), label: 'Bus stops of this operator' }}
            id={stop.id}
          />
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              variant="outlined"
              color={stop.enabled ? 'success' : 'default'}
              label={stop.enabled ? 'Enabled' : 'Disabled'}
            />
            <PermissionGate perm="console_fleet_manage">
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => navigate(`/bus-stops/${id}/${v}`)}>
          {tabs.map((t) => (
            <Tab key={t.id} value={t.id} label={t.label} />
          ))}
        </Tabs>
      </Box>

      <TabPane show={activeTab === 'general'}>
        <BusStopGeneralTab stop={stop} onSaved={onSaved} />
      </TabPane>
      <TabPane show={activeTab === 'used-by'}>
        <UsedByPanel resource="route_stops" id={stop.id} />
      </TabPane>
      <TabPane show={activeTab === 'history'}>
        <HistoryPanel contentType="demo.vehicle_route_stop" objectPk={String(stop.id)} onRestored={onSaved} />
      </TabPane>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        Last updated {formatRelativeTime(stop.updated_ts)} ·{' '}
        <RouterLink to={busStopsHref({ tenant: stop.tenant.id })} style={{ color: '#0a1a3f' }}>
          ← Back to bus stops
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this bus stop?"
        message={`"${stop.name}" will be deleted. This cannot be undone. If a line uses it, the delete is blocked.`}
        confirmLabel="Delete bus stop"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </Box>
  );
}

function TabPane({ show, children }: { show: boolean; children: ReactNode }) {
  if (!show) return null;
  return <Box>{children}</Box>;
}
